import { getAuthHeaders, authedFetch } from './authService';
const HOSTNAME = process.env.REACT_APP_API_HOSTNAME ?? 'http://localhost:8000';

const notifyServiceError = (msg: string) => {
  try { window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: msg } })); } catch (e) { /* ignore */ }
};

export type GuardApiItem = {
  id: number;
  name: string;
  phone?: string;
  active?: boolean;
  status?: string;
  photo_url?: string | null;
  id_document_url?: string | null;
  home_node?: number | null;
  start_time?: string | null;
  duty_hours?: number | null;
};

export async function getGuards(page?: number, pageSize?: number, search?: string, nodeId?: number, active?: boolean): Promise<{ guards: GuardApiItem[]; meta?: any }> {
  const params = new URLSearchParams();
  if (typeof page === 'number') params.append('page', String(page));
  if (typeof pageSize === 'number') params.append('page_size', String(pageSize));
  if (search) params.append('search', search);
  if (typeof nodeId === 'number') params.append('node', String(nodeId));
  if (typeof active === 'boolean') params.append('active', String(active));
  const q = params.toString();
  const url = `${HOSTNAME}/adminportal/guards${q ? `?${q}` : ''}`;
  const res = await authedFetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    const msg = `Failed to fetch guards: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  const body = await res.json().catch(() => null);
  const guards = body?.data?.guards ?? body?.guards ?? [];
  const meta = body?.data?.meta ?? body?.meta ?? null;
  return { guards: Array.isArray(guards) ? guards : [], meta };
}

/**
 * Create a guard
 * POST /adminportal/guards/create
 */
export async function createGuard(payload: any): Promise<any> {
  const url = `${HOSTNAME}/adminportal/guards/create`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const txt = body?.message ?? await res.text().catch(() => '');
    const msg = `Failed to create guard: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  return body;
}

/**
 * Upload guard photo
 * POST /adminportal/guards/:id/upload-photo (multipart form key: photo)
 *
 * When `options.initial` is true, the request is marked as part of the initial
 * guard-create flow (KAN-143). The backend skips writing a separate "update"
 * audit row in that case — the single "create" audit already covers it.
 */
export async function uploadGuardPhoto(guardId: number, file: File, options?: { initial?: boolean }): Promise<any> {
  const qs = options?.initial ? '?initial_upload=true' : '';
  const url = `${HOSTNAME}/adminportal/guards/${guardId}/upload-photo${qs}`;
  const fd = new FormData();
  fd.append('photo', file);
  const res = await authedFetch(url, { method: 'POST', headers: { ...getAuthHeaders() }, body: fd });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const txt = body?.message ?? await res.text().catch(() => '');
    const msg = `Failed to upload guard photo: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  return body;
}

/**
 * Upload guard ID document
 * POST /adminportal/guards/:id/upload-id (multipart form key: document)
 *
 * When `options.initial` is true, the request is marked as part of the initial
 * guard-create flow (KAN-143). The backend skips writing a separate "update"
 * audit row in that case — the single "create" audit already covers it.
 */
export async function uploadGuardDocument(guardId: number, file: File, options?: { initial?: boolean }): Promise<any> {
  const qs = options?.initial ? '?initial_upload=true' : '';
  const url = `${HOSTNAME}/adminportal/guards/${guardId}/upload-id${qs}`;
  const fd = new FormData();
  fd.append('document', file);
  const res = await authedFetch(url, { method: 'POST', headers: { ...getAuthHeaders() }, body: fd });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const txt = body?.message ?? await res.text().catch(() => '');
    const msg = `Failed to upload guard document: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  return body;
}

/**
 * Set guard active flag
 * POST /adminportal/guards/:id/active
 * body: { active: boolean }
 */
export async function setGuardActive(guardId: number, active: boolean): Promise<any> {
  const url = `${HOSTNAME}/adminportal/guards/${guardId}/active`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ active }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const txt = body?.message ?? await res.text().catch(() => '');
    const msg = `Failed to set guard active: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  return body;
}

/**
 * Update a guard
 * PATCH /adminportal/guards/:id/edit
 */
export async function updateGuard(guardId: number, payload: any): Promise<any> {
  const url = `${HOSTNAME}/adminportal/guards/${guardId}/edit`;
  const res = await authedFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const txt = body?.message ?? await res.text().catch(() => '');
    const msg = `Failed to update guard: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  return body;
}
